import { h, Fragment } from 'preact';
import { useRef, useEffect } from 'preact/hooks';
import PropTypes from 'prop-types';
import Siema from 'siema';
import { memo } from 'preact/compat';

import { hideElement, unHideElement } from '../../../../utils/dom-utils';
import { isWidthDeviceMobile } from '../../../../utils/device-utils';
import { className } from '../../../../utils/classnames-utils';
import { useArrayRef } from '../../../hooks/use-array-ref';
import { IconArrowPlayerDx, IconArrowPlayerSx } from '../../1-foundation/icons/icons';
import Card from '../../3-molecules/card/card';
import SkyItBox from '../../3-molecules/sky-it-box/sky-it-box';

const SkyItContentSlider = ({ title, image, slides = [], boxOptions, intcmpCard }) => {
  const sliderRef = useRef();
  const sliderInstance = useRef();
  const prevButton = useRef();
  const nextButton = useRef();
  const [$dots, dotRef] = useArrayRef();
  const isMobile = isWidthDeviceMobile();
  const slidesToShow = isMobile ? 2 : 4;

  const sliderClassName = className({
    'c-sky-it-content-slider ': true,
    'c-sky-it-content-slider--mobile': isMobile,
  });

  const activeDot = (index) => {
    $dots.forEach(($dot) => {
      $dot.removeAttribute('data-state');
    });
    $dots?.[index]?.setAttribute('data-state', 'active');
  };

  const handleDotClick = (index) => {
    activeDot(index);
    sliderInstance.current.goTo(index * slidesToShow);
  };

  useEffect(() => {
    sliderInstance.current = new Siema({
      selector: sliderRef.current,
      perPage: slidesToShow,
      onInit: () => {
        hideElement(prevButton.current);
      },
      onChange: () => {
        sliderInstance.current.drag.preventClick = true; // Prevent click on the card when dragging
        const isLastSlidesGroup = slides.length - sliderInstance.current.currentSlide - slidesToShow === 0;

        if (isMobile) {
          activeDot(Math.ceil(sliderInstance.current.currentSlide / slidesToShow));
        } else {
          // Prev button management
          if (sliderInstance.current.currentSlide === 0) {
            hideElement(prevButton.current);
          } else {
            unHideElement(prevButton.current);
          }

          // Next button management
          if (isLastSlidesGroup) {
            hideElement(nextButton.current);
          } else {
            unHideElement(nextButton.current);
          }
        }
      },
    });

    if (isMobile) {
      activeDot(0);
    }

    return () => sliderInstance.current.destroy();
  }, []);

  return (
    <SkyItBox title={title} image={image} options={boxOptions}>
      <div className={sliderClassName}>
        <div ref={sliderRef}>
          {slides.map((slide) => (
            <Card
              key={slide}
              newsData={slide}
              time={slide.time}
              timePosition="bottom"
              modifiers={['CA15-m', 'CA15-t', 'CA15-d']}
              intcmpCard={intcmpCard}
            />
          ))}
        </div>
        {isMobile && (
          <div className="c-sky-it-content-slider__dots">
            {slides
              .filter((_, index) => index % slidesToShow === 0)
              .map((_, index) => (
                <button type="button" key={index} ref={dotRef} onClick={() => handleDotClick(index)} />
              ))}
          </div>
        )}
        {!isMobile && (
          <Fragment>
            <button
              ref={prevButton}
              type="button"
              className="c-sky-it-content-slider__prev"
              onClick={() => sliderInstance.current.prev(slidesToShow)}
            >
              <IconArrowPlayerSx modifiers={['small']} />
            </button>
            <button
              ref={nextButton}
              type="button"
              className="c-sky-it-content-slider__next"
              onClick={() => sliderInstance.current.next(slidesToShow)}
            >
              <IconArrowPlayerDx modifiers={['small']} />
            </button>
          </Fragment>
        )}
      </div>
    </SkyItBox>
  );
};

SkyItContentSlider.propTypes = {
  title: PropTypes.string.isRequired,
  image: PropTypes.string,
  slides: PropTypes.array.isRequired,
  boxOptions: PropTypes.array,
  intcmpCard: PropTypes.string,
};

// Memoize when the new slides are the same as the previous
const mapTitle = (slide) => slide.title;
export default memo(
  SkyItContentSlider,
  (prev, next) => prev.slides?.map(mapTitle).join('') === next.slides?.map(mapTitle).join('')
);
